import React from 'react'
import { ConfigObject } from '../../../../../utils/deviceConfig'
import { DisplayBasicConfigUI } from './DisplayBasicConfigUI'
import {
    buildDisplayConfigDiffFromState,
    defaultDisplayConfigFunctions,
    DefaultDisplayConfigSection,
    getInitialDisplayConfig
} from './convertDisplay'
import { Direction, Orientation, ConnectionType } from './display.enums'
import { DebugJson } from '../../../../../components/DebugJson'
import { DeviceDataType } from '../../../device-types'
import { useFormSaveStore } from '../../../../../utils'
import { launchToast, OverlaySpinner, ToastTypes } from '../../../../../components'
import { validateBasicConfig, ValidationErrors } from '../../utils/ValidationUtils'
import { getStationsForSiblingLot } from '../TerminalBasicConfig/convertTerminal'

export interface DisplayBasicConfigProps {
    deviceData: DeviceDataType
    selectedDevice: any
    editDiffConfig: (deviceId: string, config: any, resetFunc?: () => void) => void
    setDeviceForceUpdateAction: (deviceId: string) => void
    loadingSaveBasicConfig: boolean
    loadingForceUpdate: boolean
}

export interface DisplayBasicConfigState {
    //General
    lot_id: string
    test_mode: boolean
    //Networking
    lot_mqtt_ip: ConnectionType
    //Detections
    disable_gui_event_publish: boolean
    gate_id: number
    direction: Direction
    min_confidence_level: number
    //Display
    monitor_orientation: Orientation
    resolution_width: number
    resolution_height: number
    night_brightness: number
    day_brightness: number
}

export const DisplayBasicConfig: React.FC<DisplayBasicConfigProps> = ({
    deviceData,
    selectedDevice,
    editDiffConfig,
    loadingSaveBasicConfig,
    loadingForceUpdate,
    setDeviceForceUpdateAction
}) => {
    const { base, diff } = deviceData?.config || {}
    const hasChanges = useFormSaveStore((state) => state.hasChanges)
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)

    const [displayBasicState, setDisplayBasicState] = React.useState<DisplayBasicConfigState | null>({
        ...(getInitialDisplayConfig(base, diff) as unknown as DisplayBasicConfigState)
    })
    const avaiableLots = getStationsForSiblingLot(diff)
    const [tempBuiltDiff, setTempBuiltDiff] = React.useState<ConfigObject | null>({})
    const [isValid, setIsValid] = React.useState<boolean>(false)
    const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>({})

    React.useEffect(() => {
        const errors = validateBasicConfig(displayBasicState)
        setValidationErrors(errors)
        setIsValid(Object.keys(errors).length === 0)
    }, [displayBasicState])

    const handleStateUpdate = (key: keyof DisplayBasicConfigState, value: any) => {
        setDisplayBasicState((prev) => {
            if (!prev) return prev
            return {
                ...prev,
                [key]: value
            }
        })
        setFormChanges(true)
    }
    const setDefaultToBaseForSection = (section: DefaultDisplayConfigSection) => {
        setFormChanges(true)
        const defaultSection = defaultDisplayConfigFunctions[section](base)
        setDisplayBasicState({
            ...displayBasicState,
            ...(defaultSection as DisplayBasicConfigState)
        })
    }
    const handleSave = () => {
        if (!isValid) {
            launchToast(ToastTypes.Error, 'Please fill all the errors in the form first!')
            return
        }
        // console.log(displayBasicState, 'state')
        editDiffConfig(selectedDevice?.uuid, tempBuiltDiff, () => {
            setFormChanges(false)
        })
    }

    const handleForceUpdate = () => {
        if (window.confirm('Are you sure you want to force update the configuration for this device?')) {
            setDeviceForceUpdateAction(selectedDevice?.uuid)
        }
        // console.log('Force Update')
    }
    // Build the diff when the state changes
    React.useEffect(() => {
        setTempBuiltDiff(buildDisplayConfigDiffFromState(displayBasicState, base, diff))
    }, [displayBasicState])

    return displayBasicState ? (
        <OverlaySpinner loadingTop={true} loading={loadingSaveBasicConfig || loadingForceUpdate}>
            <DisplayBasicConfigUI
                avaiableLots={avaiableLots}
                setDefaultToBaseForSection={setDefaultToBaseForSection}
                data={displayBasicState}
                onUpdate={handleStateUpdate}
                onSave={handleSave}
                onForceUpdate={handleForceUpdate}
                disabled={!hasChanges}
                forceUpdateDisabled={
                    !!deviceData.config_force_update ||
                    deviceData.config_current_timestamp === deviceData.config_target_timestamp
                }
                validationErrors={validationErrors}
                loadingSave={loadingSaveBasicConfig}
                loadingForceUpdate={loadingForceUpdate}
            />
            {/* <DebugJson src={tempBuiltDiff} showing={true} /> */}
        </OverlaySpinner>
    ) : null
}
